import { focusFormField } from '@/lib/validation/form-focus';
import { useEffect, useRef } from 'react';
import { AlertCircleIcon } from '@/components/icons';
import {
  fieldLabel,
  fieldPath,
  flattenFormErrors,
  type FormIssue,
} from '@/lib/validation/form-errors';

type ErrorWithDetails = { message: string; summaryMessage?: string; details?: FormIssue[] };

export function FormErrors({
  errors,
  issues = [],
  error,
  labels = {},
  autoFocus = true,
}: {
  autoFocus?: boolean;
  errors?: unknown;
  issues?: FormIssue[];
  error?: ErrorWithDetails | null | undefined;
  labels?: Record<string, string>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const all = [...flattenFormErrors(errors), ...issues, ...(error?.details ?? [])];
  const unique = all.filter(
    (issue, index) =>
      all.findIndex(
        (other) =>
          fieldPath(other.field) === fieldPath(issue.field) && other.message === issue.message,
      ) === index,
  );
  const visible = unique.length > 0 || !!error;
  useEffect(() => {
    if (visible && autoFocus) ref.current?.focus();
  }, [visible, autoFocus]);
  if (!visible) return null;
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      className="rounded-[10px] border border-red-200 bg-red-50/80 p-4 text-sm text-red-800 outline-none focus-visible:ring-2 focus-visible:ring-red-300"
    >
      <div className="flex items-start gap-2">
        <AlertCircleIcon className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold">
            {unique.length
              ? `${unique.length} point${unique.length > 1 ? 's' : ''} à corriger avant d’enregistrer`
              : 'Enregistrement impossible'}
          </p>
          {error && (
            <p className="mt-1 whitespace-pre-line">{error.summaryMessage ?? error.message}</p>
          )}
          {unique.length > 0 && (
            <ul className="mt-2 space-y-1">
              {unique.map((issue, index) => (
                <li key={`${issue.field}-${index}`}>
                  <button
                    type="button"
                    className="text-left underline decoration-red-300 underline-offset-2 hover:decoration-red-800 focus-visible:outline-2"
                    onClick={() =>
                      focusFormField(ref.current?.closest('form') ?? null, issue.field)
                    }
                  >
                    <span className="font-semibold">{fieldLabel(issue.field, labels)}</span> :{' '}
                    {issue.message}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
